/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import { useEffect, useState, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { getTotalPages } from '@/utils/pagination'
import { useHandlerFetchErrors } from './useHandlerFetchErrors'
import { useContextError } from './userContextError'

export const useFetchItemsWithParamsV2 = ({
  fetchService,
  fetchParams = {}
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const handleFetchErrors = useHandlerFetchErrors()
  const { clearError } = useContextError()
  const { getAccessTokenSilently: getAccessToken } = useAuth0()

  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [itemsResponse, setItemsResponse] = useState()

  const fetchData = useCallback(async () => {
    try {
      const params = Object.fromEntries(searchParams)
      setCurrentPage(params.page || 1)
      clearError()
      setLoading(true)

      const items = await fetchService(
        {
          ...fetchParams,
          searchParams: params
        },
        getAccessToken
      )
      setItemsResponse(items)
    } catch (error) {
      handleFetchErrors(error)
    } finally {
      setLoading(false)
    }
  }, [
    searchParams,
    fetchService,
    fetchParams,
    getAccessToken,
    clearError,
    handleFetchErrors
  ])

  const removeUrlParams = (...paramNames) => {
    setSearchParams((prevParams) => {
      const updatedParams = new URLSearchParams(prevParams)

      paramNames.forEach(param => updatedParams.delete(param))

      return updatedParams
    })
  }
  const updateParam = ({ param, value }) => {
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams)
      newParams.set(param, value)

      return newParams
    })
  }

  const onChangePage = (page) => {
    setCurrentPage(page)
    updateParam({ param: 'page', value: page })
  }

  const reloadItems = async () => {
    await fetchData()
  }

  const totalPages = itemsResponse && getTotalPages(itemsResponse.total)

  useEffect(() => {
    fetchData()
  }, [searchParams.toString()])

  const params = Object.fromEntries(searchParams)

  return {
    itemsResponse,
    loading,
    onChangePage,
    page: currentPage,
    reloadItems,
    removeUrlParams,
    searchParams: params,
    setPage: setCurrentPage,
    setSearchParams,
    totalPages,
    updateParam
  }
}
